import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbCalendar, NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { Company } from 'src/app/domains/companies/models/company.model';
import { CatalogType, ConnectionEquipmentDetails, ConnectionEquipmentInfo, ConnectivityCatalogType, ConnectivityEquipmentStatus } from 'src/app/domains/connectivity/models/connectivity.model';

const DATE = new Date(Date.now());
const ACTIVATION_DATE = NgbDate.from({
  year: DATE.getFullYear(),
  month: DATE.getMonth() + 1,
  day: DATE.getDate()
});

@Component({
  selector: 'gdp-add-edit-connectivity-equipment-form',
  templateUrl: './add-edit-connectivity-equipment-form.component.html',
  styleUrls: ['./add-edit-connectivity-equipment-form.component.scss']
})
export class AddEditConnectivityEquipmentFormComponent implements OnInit {
  @Input() equipment: ConnectionEquipmentDetails;
  @Input() loading: boolean;
  @Input() company: Company;
  @Input() connectionId: number;
  @Output() onSubmit: EventEmitter<any> = new EventEmitter<any>();
  public form: UntypedFormGroup;
  public startDay: NgbDate = this.ngbCalendar.getToday();
  public statuses: string[] = ConnectivityEquipmentStatus;
  public selectedStatus: string = this.statuses[0];
  public types: string[] = CatalogType;
  public type = ConnectivityCatalogType;
  public selectedType: ConnectivityCatalogType = ConnectivityCatalogType.internet_key;
  public macAddressPattern = /^([0-9A-Fa-f]{12}|([0-9A-Fa-f]{2}-){5}[0-9A-Fa-f]{2}|([0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2})$/;
  public imeiPattern = /^\d{15,17}$/;
  constructor(private formBuilder: UntypedFormBuilder, private ngbCalendar: NgbCalendar) { }

  ngOnInit(): void {
    if (this.equipment) {
      this.selectedType = this.equipment.equipment_type;
      if (this.equipment.equipment_type === ConnectivityCatalogType.internet_key) {
        this.form = this.formBuilder.group({
          company_id: this.company.companyId,
          connection_id: this.connectionId,
          type: this.equipment.equipment_type,
          wifi_equipment_catalog_entry_id: new UntypedFormControl(this.equipment.equipment_catalog_entry),
          serial_number: new UntypedFormControl(this.equipment.serial_number, Validators.required),
          number: new UntypedFormControl(this.equipment.number, Validators.required),
          iccid: new UntypedFormControl(this.equipment.iccid, Validators.required),
          pin: new UntypedFormControl(this.equipment.pin, Validators.required),
          puk: new UntypedFormControl(this.equipment.puk, Validators.required),
          status: this.equipment.status,
          activation_date: new UntypedFormControl(ACTIVATION_DATE, Validators.required),
          imei: new UntypedFormControl(this.equipment.imei, [Validators.required, Validators.pattern(this.imeiPattern)]),
        });
      }
      if (this.equipment.equipment_type === ConnectivityCatalogType.modem_router) {
        this.form = this.formBuilder.group({
          type: this.equipment.equipment_type,
          company_id: this.company.companyId,
          connection_id: this.connectionId,
          wifi_equipment_catalog_entry_id: new UntypedFormControl(this.equipment.equipment_catalog_entry),
          company_name: this.equipment.company_name,
          mac_address: new UntypedFormControl(this.equipment.mac_address, [Validators.required, Validators.pattern(this.macAddressPattern)])

        });
      }
    }
    else {
      this.initFormFields(ConnectivityCatalogType.internet_key);
    }
  }

  initFormFields(type: string) {
    if (type === ConnectivityCatalogType.internet_key) {
      return this.form = this.formBuilder.group({
        company_id: this.company.companyId,
        connection_id: this.connectionId,
        type: type,
        wifi_equipment_catalog_entry_id: new UntypedFormControl(null),
        serial_number: new UntypedFormControl(null, Validators.required),
        number: new UntypedFormControl(null, Validators.required),
        iccid: new UntypedFormControl(null, Validators.required),
        pin: new UntypedFormControl(null, Validators.required),
        puk: new UntypedFormControl(null, Validators.required),
        status: this.statuses[0],
        activation_date: new UntypedFormControl(ACTIVATION_DATE, Validators.required),
        imei: new UntypedFormControl(null, Validators.required),
      });
    }
    if (type === ConnectivityCatalogType.modem_router) {
      this.form = this.formBuilder.group({
        type: ConnectivityCatalogType.modem_router,
        company_id: this.company.companyId,
        connection_id: this.connectionId,
        company_name: this.company.name,
        wifi_equipment_catalog_entry_id: new UntypedFormControl(null),
        mac_address: new UntypedFormControl(null, [Validators.required, Validators.pattern(this.macAddressPattern)])
      });
    }
  }

  isFieldValid(name: string) {
    return (
      !this.form.get(name).valid && this.form.get(name).touched
    );
  }

  submit() {
    const activationDateFormValue = this.form.value.activation_date;
    const activationDate = activationDateFormValue ? new Date(activationDateFormValue.year, activationDateFormValue.month - 1, activationDateFormValue.day).toISOString() : null;
    if (this.form.valid) {

      const newEquipment: ConnectionEquipmentInfo = {
        ...this.form.value,
        activation_date: activationDate
      };
      if (newEquipment.wifi_equipment_catalog_entry_id) {
        this.onSubmit.emit({ ...newEquipment, wifi_equipment_catalog_entry_id: this.form.get('wifi_equipment_catalog_entry_id').value.id });
      }
      else {
        delete newEquipment.wifi_equipment_catalog_entry_id;
        this.onSubmit.emit(newEquipment);
      }
    }
  }

  onChangeStatus(status: string): void {
    this.form.patchValue({ status: status });
  }

  onChangeType(type: ConnectivityCatalogType): void {
    this.selectedType = type;
    this.initFormFields(this.selectedType);
  }
}
